type FpsOverlayOptions = {
    /** Warn threshold FPS */
    warnFps?: number;          // default 15
    /** How long FPS must stay below threshold to show "*" (ms) */
    warnAfterMs?: number;      // default 1000
    /** Update text at most this often (ms) */
    updateEveryMs?: number;    // default 250
    /** Optional: custom z-index */
    zIndex?: number;           // default 2147483647
};

const DISABLE:boolean = true;

export function startFpsOverlay(options: FpsOverlayOptions = {}) {
    if (DISABLE) return { element: null, stop: () => {} };

    const warnFps = options.warnFps ?? 15;
    const warnAfterMs = options.warnAfterMs ?? 1000;
    const updateEveryMs = options.updateEveryMs ?? 250;
    const zIndex = options.zIndex ?? 2147483647;

    // --- overlay element ---
    const el = document.createElement("div");
    el.setAttribute("data-cesdk-fps-overlay", "true");
    el.style.position = "fixed";
    el.style.top = "10px";
    el.style.right = "10px";
    el.style.zIndex = String(zIndex);
    el.style.pointerEvents = "none";
    el.style.fontFamily = `ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace`;
    el.style.fontSize = "12px";
    el.style.lineHeight = "1";
    el.style.padding = "6px 8px";
    el.style.borderRadius = "8px";
    el.style.background = "rgba(0,0,0,0.65)";
    el.style.color = "#fff";
    //el.style.boxShadow = "0 2px 12px rgba(0,0,0,0.25)";
    el.textContent = "FPS: --";
    document.documentElement.appendChild(el);

    // --- fps measurement ---
    let rafId = 0;

    let lastFrameTs = performance.now();
    let frameCount = 0;
    let lastUpdateTs = lastFrameTs;

    // Tracks continuous time spent under warnFps
    let lowStreakMs = 0;
    let lastLoopTs = lastFrameTs;

    let lastDisplayed = ""; // avoid DOM churn

    const loop = (ts: number) => {
        const dt = ts - lastFrameTs;
        lastFrameTs = ts;

        frameCount++;

        // streak uses actual elapsed time, not frame count
        const loopDt = ts - lastLoopTs;
        lastLoopTs = ts;

        // Update display every updateEveryMs for stability
        if (ts - lastUpdateTs >= updateEveryMs) {
            const elapsed = ts - lastUpdateTs;
            const fps = (frameCount * 1000) / elapsed;

            // Update low-FPS streak based on *current* fps
            if (fps < warnFps) lowStreakMs += elapsed;
            else lowStreakMs = 0;

            const warn = lowStreakMs >= warnAfterMs ? " *" : "";
            const text = `FPS: ${Math.round(fps)}${warn}`;

            if (text !== lastDisplayed) {
                el.textContent = text;
                lastDisplayed = text;
            }

            // reset window counters
            frameCount = 0;
            lastUpdateTs = ts;
        }

        rafId = requestAnimationFrame(loop);
    };

    rafId = requestAnimationFrame(loop);

    // return a stop handle so you can clean it up
    return {
        element: el,
        stop() {
            cancelAnimationFrame(rafId);
            el.remove();
        },
    };
}
